import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { fetchPersonalData } from '../../features/profile-settings/profile-settings.slice';
import { hasCompleteBankInfo } from '../../features/bank/bank.helpers';
import { fetchBankIdVerificationStatus } from '../../features/bankid/bankid.slice';
import './onboarding-progress-card.style.scss';

/**
 * Dashboard card that shows how far the user is through onboarding (a radial
 * chart + guided per-step breakdown) and lets them jump back into the wizard.
 * Self-hiding: it fetches bank + ID verification status itself (read-only GETs)
 * and only renders while at least one step is still unfinished — regardless of
 * the IsCloseSteps flag. Nothing existing is affected.
 *
 * Bank status reads from profile-settings.slice's fetchPersonalData
 * (/employee/get/{userId}) — the same confirmed-correct source used by
 * profile settings, onboarding, and the invoice-creation bank checks — rather
 * than the legacy /Settings endpoint, so isBriteIdExist/isBriteSessionAccount
 * are accounted for consistently (a Brite-linked account was previously never
 * recognized as "done" here).
 */

const StepIcon: React.FC<{ name: string }> = ({ name }) => {
  if (name === 'profile') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5.5 19c0-3.1 2.9-5.2 6.5-5.2s6.5 2.1 6.5 5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === 'bank') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 10h16M5 10l7-5 7 5M6.5 10v7M10 10v7M14 10v7M17.5 10v7M4 20h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === 'email') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.2-2.9 7.9-7 9-4.1-1.1-7-4.8-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

type OnboardingStepKey = 'profile' | 'bank' | 'email' | 'verify';

interface OnboardingProgressCardProps {
  userOnboardingData: any;
  /** Opens the wizard; the step key deep-links directly to that step. */
  onContinue: (step: OnboardingStepKey) => void;
}

export const OnboardingProgressCard: React.FC<OnboardingProgressCardProps> = ({
  userOnboardingData,
  onContinue,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const authUserId = useAppSelector((s: any) => s?.auth?.user?.userId);
  const personalData = useAppSelector((s: any) => s?.profileSettings?.personalData);
  const isIdentityVerified = useAppSelector((s: any) => s?.bankidAuthFlow?.isIdentityVerified);
  const [animateIn, setAnimateIn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [displayPct, setDisplayPct] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      dispatch(fetchPersonalData(authUserId) as any),
      dispatch(fetchBankIdVerificationStatus() as any),
    ]).finally(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [dispatch, authUserId]);

  // Kick the ring/entrance animation on the next frame.
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const isLoginWithBankId = (() => {
    try {
      return !!JSON.parse(localStorage.getItem('user') || '{}')?.isLoginWithBankId;
    } catch {
      return false;
    }
  })();

  /* Must match what the profile form itself demands (its validateForm checks
     city, country, profession, email, address, postal code and — when the
     account has none stored — a phone number). Phone and email were missing
     here, so a profile saved without a phone number was reported as Done while
     the form still refused to move past it. */
  const profileDone = !!(
    userOnboardingData?.city &&
    userOnboardingData?.country &&
    userOnboardingData?.profession &&
    userOnboardingData?.address &&
    userOnboardingData?.zipCode &&
    userOnboardingData?.phoneNo?.trim() &&
    userOnboardingData?.email?.trim()
  );
  const bankDone = hasCompleteBankInfo(personalData);
  const emailDone = !!personalData?.isVerified;
  const verifyDone = isLoginWithBankId || isIdentityVerified === true;

  const steps: { key: OnboardingStepKey; label: string; done: boolean }[] = [
    { key: 'profile', label: t('COMPLETE_PROFILE'), done: profileDone },
    { key: 'bank', label: t('BANK_DETAILS'), done: bankDone },
    { key: 'email', label: t('EMAIL_VERIFICATION'), done: emailDone },
    { key: 'verify', label: t('ID_VERIFICATION'), done: verifyDone },
  ];
  const total = steps.length;
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / total) * 100);
  const nextIndex = steps.findIndex((s) => !s.done);
  const remaining = total - doneCount;
  const nextStepKey: OnboardingStepKey = steps[nextIndex]?.key ?? 'profile';

  // Count the percentage up as the ring sweeps in.
  useEffect(() => {
    if (!animateIn) return;
    let raf = 0;
    let start: number | null = null;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / 900);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayPct(Math.round(eased * pct));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animateIn, pct]);

  // Only render while onboarding still has an unfinished step.
  if (!loaded || doneCount === total) return null;

  const R = 58;
  const CIRC = 2 * Math.PI * R;
  const dashOffset = CIRC * (1 - (animateIn ? pct : 0) / 100);

  return (
    <div className={`onb-progress-card${animateIn ? ' is-in' : ''}`}>
      <span className="onb-progress-card__glow" aria-hidden="true" />

      <div className="onb-progress-card__head">
        <div className="onb-progress-card__head-text">
          <h3 className="onb-progress-card__title">{t('ONBOARDING_CARD_TITLE')}</h3>
          <p className="onb-progress-card__subtitle">{t('ONBOARDING_INCOMPLETE_WARNING')}</p>
        </div>
        <span className="onb-progress-card__pill">
          <span className="onb-progress-card__pill-dot" />
          {remaining === 1 ? t('ONBOARDING_CARD_ALMOST') : `${remaining} ${t('ONBOARDING_CARD_TO_GO')}`}
        </span>
      </div>

      <div className="onb-progress-card__body">
        {/* Radial completion chart */}
        <div className="onb-progress-card__chart">
          <svg viewBox="0 0 140 140" width="150" height="150" aria-hidden="true">
            <defs>
              <linearGradient id="onbProgressGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6750b8" />
                <stop offset="55%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
            <circle cx="70" cy="70" r={R} className="onb-progress-card__ring-track" />
            <circle
              cx="70"
              cy="70"
              r={R}
              className="onb-progress-card__ring-value"
              stroke="url(#onbProgressGrad)"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 70 70)"
            />
          </svg>
          <div className="onb-progress-card__chart-center">
            <span className="onb-progress-card__pct">{displayPct}%</span>
            <span className="onb-progress-card__count">
              {doneCount}/{total} {t('ONBOARDING_CARD_STEPS_DONE')}
            </span>
          </div>
        </div>

        {/* Guided per-step breakdown */}
        <ul className="onb-progress-card__steps">
          {steps.map((s, i) => {
            const state = s.done ? 'done' : i === nextIndex ? 'next' : 'future';
            const isNext = state === 'next';
            return (
              <li
                key={s.key}
                className={`onb-progress-card__step is-${state}`}
                {...(isNext
                  ? { role: 'button', tabIndex: 0, onClick: () => onContinue(s.key), onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onContinue(s.key); } }
                  : {})}
              >
                <span className="onb-progress-card__step-badge">
                  {s.done ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <StepIcon name={s.key} />
                  )}
                </span>
                <span className="onb-progress-card__step-label">{s.label}</span>
                {s.done ? (
                  <span className="onb-progress-card__step-tag onb-progress-card__step-tag--done">{t('ONBOARDING_CARD_DONE')}</span>
                ) : isNext ? (
                  <span className="onb-progress-card__step-tag onb-progress-card__step-tag--next">
                    {t('ONBOARDING_CARD_NEXT')}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="onb-progress-card__footer">
        <button type="button" className="onb-progress-card__btn" onClick={() => onContinue(nextStepKey)}>
          {t('ONBOARDING_CONTINUE')}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default OnboardingProgressCard;
